<template>
  <div class="dr">
    <header class="dr-head">
      <div>
        <h1 class="dr-title">État des livreurs — {{ cityLabel }}</h1>
        <p class="dr-sub">Contrôle du matin : colis de la veille, retours agence et paiements.</p>
      </div>
      <div class="dr-tools">
        <label class="dr-date dr-city">
          <span>Zone</span>
          <v-select
            v-model="zoneId"
            :options="zones"
            :reduce="(z) => z.id"
            label="name"
            placeholder="Toutes les zones"
            @update:modelValue="onZoneChange"
          />
        </label>
        <label class="dr-date dr-city">
          <span>Ville</span>
          <v-select
            v-model="cityId"
            :options="zoneCities"
            :reduce="(c) => c.id"
            label="name"
            placeholder="Toutes les villes"
            @update:modelValue="onCityChange"
          />
        </label>
        <label class="dr-date">
          <span>Journée suivie</span>
          <input v-model="date" type="date" class="form-control" :max="today" @change="onFilterChange" />
        </label>
        <button type="button" class="btn btn-outline-secondary" :disabled="loading" @click="fetchReport()">
          <i class="ph ph-arrow-clockwise" aria-hidden="true"></i> Actualiser
        </button>
        <button type="button" class="btn btn-primary" :disabled="pdfLoading === 'summary'" @click="openSummaryPdf">
          <span v-if="pdfLoading === 'summary'" class="spinner-border spinner-border-sm"></span>
          <i v-else class="ph ph-file-pdf" aria-hidden="true"></i> PDF de l'état
        </button>
      </div>
    </header>

    <!-- ── KPIs ─────────────────────────────────────────────────── -->
    <div class="dr-kpis">
      <div class="dr-kpi">
        <span class="dr-kpi-icon tone-info"><i class="ph ph-moped" aria-hidden="true"></i></span>
        <div><strong>{{ totals.drivers }}</strong><span>Livreurs actifs sur la journée</span></div>
      </div>
      <div class="dr-kpi">
        <span class="dr-kpi-icon tone-success"><i class="ph ph-package" aria-hidden="true"></i></span>
        <div>
          <strong>{{ totals.delivered }} / {{ totals.assigned }}</strong>
          <span>Livrés / affectés · TDL {{ pct(totals.tdl) }}</span>
        </div>
      </div>
      <div class="dr-kpi is-hot">
        <span class="dr-kpi-icon tone-orange"><i class="ph ph-arrow-u-down-left" aria-hidden="true"></i></span>
        <div>
          <strong>{{ totals.to_return }}</strong>
          <span>À retourner à l'agence · {{ totals.received }} déjà reçu(s)</span>
        </div>
      </div>
      <div class="dr-kpi">
        <span class="dr-kpi-icon tone-warning"><i class="ph ph-wallet" aria-hidden="true"></i></span>
        <div><strong>{{ money(totals.pay_amount) }}</strong><span>Bons de paiement ({{ $devis }})</span></div>
      </div>
    </div>

    <section class="dr-card">
      <div class="dr-filters">
        <div class="dr-search">
          <i class="ph ph-magnifying-glass" aria-hidden="true"></i>
          <input v-model="search" type="search" placeholder="Rechercher un livreur…" aria-label="Rechercher un livreur" />
        </div>
        <span class="dr-period">
          Journée suivie <b>{{ fmt(report.date) }}</b> · contrôle du <b>{{ fmt(report.control_date) }}</b>
        </span>
      </div>

      <div class="table-responsive tr-scroll dr-table-wrap">
        <table class="table dr-table align-middle mb-0">
          <thead>
            <tr>
              <th>Livreur</th>
              <th class="text-center">Affectés</th>
              <th class="text-center">Livrés</th>
              <th class="text-center">TDL (%)</th>
              <th class="text-center">Refusés</th>
              <th class="text-center">En cours</th>
              <th class="text-center">Annulés</th>
              <th class="text-center">Reportés</th>
              <th class="text-center">Colis remboursés</th>
              <th class="text-center">Colis échangés</th>
              <th class="text-center dr-hot-head">À retourner à l'agence</th>
              <th class="text-end">Bon de paiement ({{ $devis }})</th>
              <th class="text-center">Retours reçus</th>
              <th class="text-center">Détail</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="14" class="text-center py-4"><span class="spinner-border spinner-border-sm"></span></td>
            </tr>
            <tr v-else-if="!report.rows.length">
              <td colspan="14" class="dr-empty">
                <i class="ph ph-moped" aria-hidden="true"></i>
                {{ search ? "Aucun livreur ne correspond à votre recherche." : emptyMessage }}
              </td>
            </tr>
            <template v-else>
              <template v-for="row in report.rows" :key="row.driver_id">
              <tr>
                <td class="dr-driver" data-label="Livreur">
                  <strong>{{ row.driver_name }}</strong>
                  <span v-if="!row.active" class="dr-tag">Inactif</span>
                  <small v-if="row.city_name" class="d-block dr-city-name">
                    <i class="ph ph-map-pin" aria-hidden="true"></i> {{ row.city_name }}
                  </small>
                  <small v-if="row.phone" class="d-block text-muted">{{ row.phone }}</small>
                </td>
                <td class="text-center" data-label="Affectés">{{ row.assigned }}</td>
                <td class="text-center" data-label="Livrés">{{ row.delivered }}</td>
                <td class="text-center" data-label="TDL (%)">
                  <span class="dr-tdl" :class="tdlTone(row.tdl)">{{ pct(row.tdl) }}</span>
                </td>
                <td class="text-center" data-label="Refusés">{{ row.refused }}</td>
                <td class="text-center" data-label="En cours">{{ row.in_progress }}</td>
                <td class="text-center" data-label="Annulés">{{ row.cancelled }}</td>
                <td class="text-center" data-label="Reportés">{{ row.postponed }}</td>
                <td class="text-center" data-label="Colis remboursés">
                  <button v-if="row.refunded" type="button" class="dr-link" @click="toggle(row.driver_id)">{{ row.refunded }}</button>
                  <template v-else>0</template>
                </td>
                <td class="text-center" data-label="Colis échangés">
                  <button v-if="row.exchanged" type="button" class="dr-link" @click="toggle(row.driver_id)">{{ row.exchanged }}</button>
                  <template v-else>0</template>
                </td>
                <td class="text-center dr-hot" data-label="À retourner à l'agence"><strong>{{ row.to_return }}</strong></td>
                <td class="text-end text-nowrap" data-label="Bon de paiement" :title="row.pay_refs.join(', ')">
                  {{ money(row.pay_amount) }}
                  <small v-if="row.pay_refs.length" class="d-block text-muted">{{ row.pay_refs.length }} bon(s)</small>
                </td>
                <td class="text-center" data-label="Retours reçus">
                  <span v-if="row.to_return" class="dr-received" :class="{ 'is-done': row.received >= row.to_return }">
                    {{ row.received }} / {{ row.to_return }}
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td class="text-center dr-actions" data-label="Détail">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary dr-act"
                    :disabled="pdfLoading === row.driver_id"
                    :aria-label="`PDF détaillé de ${row.driver_name}`"
                    @click="openDriverPdf(row)"
                  >
                    <span v-if="pdfLoading === row.driver_id" class="spinner-border spinner-border-sm"></span>
                    <i v-else class="ph ph-file-pdf" aria-hidden="true"></i> PDF
                  </button>
                </td>
              </tr>
              <tr v-if="expanded.has(row.driver_id)" class="dr-detail-row">
                <td colspan="14">
                  <div v-if="row.exchanged_list?.length" class="dr-detail">
                    <h6><i class="ph ph-arrows-left-right" aria-hidden="true"></i> Colis échangés livrés ({{ row.exchanged_list.length }})</h6>
                    <table class="table table-sm mb-2">
                      <thead><tr><th>Code</th><th>Client</th><th>Destinataire</th><th>Téléphone</th><th>Ville</th><th class="text-end">Prix ({{ $devis }})</th><th>Livré le</th></tr></thead>
                      <tbody>
                        <tr v-for="p in row.exchanged_list" :key="p.code">
                          <td><b>{{ p.code }}</b></td><td>{{ p.customer_name }}</td><td>{{ p.receiver }}</td><td>{{ p.phone }}</td><td>{{ p.city_name }}</td>
                          <td class="text-end">{{ money(p.price) }}</td><td>{{ fmt(p.delivery_time) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-if="row.refunded_list?.length" class="dr-detail">
                    <h6><i class="ph ph-receipt" aria-hidden="true"></i> Remboursements effectués ({{ row.refunded_list.length }})<span v-if="row.refunds_pending_return"> · {{ row.refunds_pending_return }} produit(s) à remettre à l'agence</span></h6>
                    <table class="table table-sm mb-0">
                      <thead><tr><th>Demande</th><th>Colis</th><th>Client</th><th>Destinataire</th><th>Téléphone</th><th class="text-end">Montant ({{ $devis }})</th><th>Retour produit</th><th>Remboursé le</th></tr></thead>
                      <tbody>
                        <tr v-for="r in row.refunded_list" :key="r.ref">
                          <td><b>{{ r.ref }}</b></td><td>{{ r.parcel_code }}</td><td>{{ r.customer_name }}</td><td>{{ r.receiver }}</td><td>{{ r.phone }}</td>
                          <td class="text-end">{{ money(r.amount) }}</td><td>{{ r.with_return ? "Oui" : "Non" }}</td><td>{{ fmt(r.refunded_at) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </td>
              </tr>
              </template>
            </template>
          </tbody>
          <tfoot v-if="!loading && report.rows.length">
            <tr>
              <td class="dr-driver">TOTAL <small class="dr-total-note">(tous les livreurs)</small></td>
              <td class="text-center" data-label="Affectés">{{ totals.assigned }}</td>
              <td class="text-center" data-label="Livrés">{{ totals.delivered }}</td>
              <td class="text-center" data-label="TDL (%)">{{ pct(totals.tdl) }}</td>
              <td class="text-center" data-label="Refusés">{{ totals.refused }}</td>
              <td class="text-center" data-label="En cours">{{ totals.in_progress }}</td>
              <td class="text-center" data-label="Annulés">{{ totals.cancelled }}</td>
              <td class="text-center" data-label="Reportés">{{ totals.postponed }}</td>
              <td class="text-center" data-label="Colis remboursés">{{ totals.refunded }}</td>
              <td class="text-center" data-label="Colis échangés">{{ totals.exchanged }}</td>
              <td class="text-center dr-hot-total" data-label="À retourner à l'agence">{{ totals.to_return }}</td>
              <td class="text-end text-nowrap" data-label="Bon de paiement">{{ money(totals.pay_amount) }}</td>
              <td class="text-center" data-label="Retours reçus">{{ totals.received }} / {{ totals.to_return }}</td>
              <td class="dr-actions"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <nav v-if="pagination.total > 0" class="dr-pager" aria-label="Pagination des livreurs">
        <div class="dr-pager-info">
          {{ pagination.from }}–{{ pagination.to }} sur {{ pagination.total }} livreur(s)
        </div>
        <div class="dr-pager-size">
          <label for="dr-per-page">Par page</label>
          <select id="dr-per-page" v-model.number="perPage" class="form-select form-select-sm" @change="onFilterChange">
            <option v-for="n in [10, 25, 50, 100]" :key="n" :value="n">{{ n }}</option>
          </select>
        </div>
        <ul v-if="pagination.last_page > 1" class="dr-pager-pages">
          <li>
            <button type="button" class="dr-page-btn" :disabled="loading || page <= 1" aria-label="Page précédente" @click="goTo(page - 1)">
              <i class="ph ph-caret-left" aria-hidden="true"></i>
            </button>
          </li>
          <li v-for="n in pageNumbers" :key="n">
            <button
              type="button"
              class="dr-page-btn"
              :class="{ 'is-active': n === page }"
              :disabled="loading"
              :aria-current="n === page ? 'page' : undefined"
              @click="goTo(n)"
            >
              {{ n }}
            </button>
          </li>
          <li>
            <button type="button" class="dr-page-btn" :disabled="loading || page >= pagination.last_page" aria-label="Page suivante" @click="goTo(page + 1)">
              <i class="ph ph-caret-right" aria-hidden="true"></i>
            </button>
          </li>
        </ul>
      </nav>

      <ul class="dr-legend">
        <li><b>TDL</b> = Livrés / Affectés × 100.</li>
        <li>
          <b class="text-orange">À retourner :</b> colis non livrés à la fin de la journée suivie (+ produits de
          remboursement avec retour) qui doivent revenir à l'agence.
        </li>
        <li><b class="text-orange">Retours reçus :</b> colis à retourner déjà enregistrés dans un bon retour agence.</li>
        <li><b class="text-orange">Bon de paiement :</b> montant net des bons de paiement livreur contenant ces colis.</li>
      </ul>
    </section>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import moment from "moment";
import api from "@/utils/axios";
import { useAppToast } from "@/composables/useAppToast";

interface ReportRow {
  driver_id: number;
  driver_name: string;
  city_name: string;
  phone: string | null;
  active: number;
  assigned: number;
  delivered: number;
  tdl: number;
  refused: number;
  in_progress: number;
  cancelled: number;
  postponed: number;
  refunded: number;
  exchanged: number;
  to_return: number;
  received: number;
  pay_amount: number;
  pay_refs: string[];
  exchanged_list?: { code: string; customer_name: string; receiver: string; phone: string; city_name: string; price: number; delivery_time: string }[];
  refunded_list?: { ref: string; parcel_code: string; customer_name: string; receiver: string; phone: string; amount: number; with_return: boolean; refunded_at: string }[];
  refunds_pending_return?: number;
}

const emptyTotals = () => ({
  drivers: 0, assigned: 0, delivered: 0, tdl: 0, refused: 0, in_progress: 0, cancelled: 0,
  postponed: 0, refunded: 0, exchanged: 0, to_return: 0, received: 0, pay_amount: 0,
});

// "État des livreurs — <ville / zone choisie>" (see App\Services\DriverReturnReport)
const CITY_KEY = "driverReturnsReport.cityId";
const ZONE_KEY = "driverReturnsReport.zoneId";

export default defineComponent({
  name: "DriverReturnsReport",
  setup() {
    const { showToast } = useAppToast();
    const today = moment().format("YYYY-MM-DD");
    const date = ref(moment().subtract(1, "day").format("YYYY-MM-DD"));
    const loading = ref(false);
    const pdfLoading = ref<number | "summary" | null>(null);
    const search = ref("");
    const page = ref(1);
    const perPage = ref(10);
    const pagination = reactive({ current_page: 1, per_page: 10, total: 0, last_page: 1, from: 0, to: 0 });
    const cities = ref<{ id: number; name: string; id_zone: number | null }[]>([]);
    const zones = ref<{ id: number; name: string }[]>([]);
    const cityId = ref<number | null>(readStored(CITY_KEY));
    const zoneId = ref<number | null>(readStored(ZONE_KEY));
    // cities offered = those of the chosen zone
    const zoneCities = computed(() =>
      zoneId.value ? cities.value.filter((c) => Number(c.id_zone) === Number(zoneId.value)) : cities.value
    );
    const report = reactive({
      date: date.value,
      city: null as { id: number; name: string } | null,
      zone: null as { id: number; name: string } | null,
      control_date: today,
      rows: [] as ReportRow[],
      totals: emptyTotals(),
    });
    const totals = computed(() => report.totals);
    const cityLabel = computed(() =>
      report.city?.name || (report.zone ? `Zone ${report.zone.name}` : "Toutes les villes")
    );
    const emptyMessage = computed(() => {
      const place = report.city?.name || (report.zone ? `la zone ${report.zone.name}` : "");
      return place ? `Aucun livreur de ${place} n'avait de colis ce jour-là.` : "Aucun livreur n'avait de colis ce jour-là.";
    });
    // filters shared by the report and its PDFs
    const params = () => ({ date: date.value, city_id: cityId.value || undefined, zone_id: zoneId.value || undefined });
    // the report itself is paginated and searched by the API
    const reportParams = () => ({
      ...params(),
      page: page.value,
      per_page: perPage.value,
      search: search.value.trim() || undefined,
    });

    function readStored(key: string): number | null {
      try {
        return Number(localStorage.getItem(key)) || null;
      } catch (e) {
        return null;
      }
    }
    function store(key: string, value: number | null) {
      try {
        if (value) localStorage.setItem(key, String(value));
        else localStorage.removeItem(key);
      } catch (e) {
        /* per-browser convenience only */
      }
    }

    // any filter change goes back to the first page
    function onFilterChange() {
      page.value = 1;
      fetchReport();
    }

    function onCityChange() {
      store(CITY_KEY, cityId.value);
      onFilterChange();
    }

    function onZoneChange() {
      store(ZONE_KEY, zoneId.value);
      // the chosen city must belong to the new zone
      if (cityId.value && !zoneCities.value.some((c) => c.id === cityId.value)) {
        cityId.value = null;
        store(CITY_KEY, null);
      }
      onFilterChange();
    }

    async function fetchCities() {
      try {
        const res = await api.get("/api/auth/citiesList");
        cities.value = res.data.map((c: any) => ({ id: c.id, name: c.name, id_zone: c.id_zone ?? null }));
      } catch (error) {
        console.error(error);
      }
    }

    async function fetchZones() {
      try {
        const res = await api.get("/api/auth/ListZone");
        zones.value = (res.data || [])
          .map((z: any) => ({ id: z.id, name: String(z.name || "").trim() }))
          .sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, "fr"));
      } catch (error) {
        console.error(error);
      }
    }

    async function fetchReport() {
      loading.value = true;
      try {
        const res = await api.get("/api/auth/driver-returns-report", { params: reportParams() });
        const { pagination: paging, ...data } = res.data;
        Object.assign(report, data);
        if (paging) {
          Object.assign(pagination, paging);
          page.value = paging.current_page; // the API clamps a page that no longer exists
        }
      } catch (error) {
        console.error(error);
        report.rows = [];
        report.totals = emptyTotals();
        Object.assign(pagination, { current_page: 1, total: 0, last_page: 1, from: 0, to: 0 });
        showToast("Impossible de charger l'état des livreurs.", "error");
      } finally {
        loading.value = false;
      }
    }

    function goTo(n: number) {
      if (n < 1 || n > pagination.last_page || n === page.value) return;
      page.value = n;
      fetchReport();
    }

    // up to 5 page buttons around the current one
    const pageNumbers = computed(() => {
      const last = pagination.last_page;
      const start = Math.max(1, Math.min(page.value - 2, last - 4));
      const end = Math.min(last, start + 4);
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    });

    // search is done by the API: wait for the typing to pause
    let searchTimer: number | undefined;
    watch(search, () => {
      window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(onFilterChange, 350);
    });
    onBeforeUnmount(() => window.clearTimeout(searchTimer));

    // Opens the PDF in a new tab (opened before the request so popup blockers let it through)
    async function openPdf(url: string, fileName: string, key: number | "summary") {
      pdfLoading.value = key;
      const popup = window.open("about:blank", "_blank");
      try {
        const response = await api.get(url, { params: params(), responseType: "blob" });
        const blobUrl = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
        if (popup) popup.location.href = blobUrl;
        else {
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = fileName;
          link.click();
        }
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      } catch (error: any) {
        popup?.close();
        let message = "Impossible de générer le PDF.";
        if (error?.response?.data instanceof Blob) {
          try {
            message = JSON.parse(await error.response.data.text()).message || message;
          } catch (e) {
            /* keep the default message */
          }
        }
        showToast(message, "error");
      } finally {
        pdfLoading.value = null;
      }
    }

    const openSummaryPdf = () =>
      openPdf("/api/auth/driver-returns-report/pdf", `etat-livreurs-${date.value}.pdf`, "summary");
    const openDriverPdf = (row: ReportRow) =>
      openPdf(
        `/api/auth/users/${row.driver_id}/driver-daily-return-report`,
        `rapport-retours-${row.driver_id}-${date.value}.pdf`,
        row.driver_id
      );

    // rows whose exchanged / refunded parcels are shown
    const expanded = ref(new Set<number>());
    function toggle(id: number) {
      const next = new Set(expanded.value);
      if (!next.delete(id)) next.add(id);
      expanded.value = next;
    }

    const money = (v: number) =>
      Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const pct = (v: number) => `${Number(v || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} %`;
    const fmt = (d: string) => (d ? moment(d).format("DD/MM/YYYY") : "");
    const tdlTone = (v: number) => (v >= 80 ? "is-good" : v >= 60 ? "is-mid" : "is-low");

    onMounted(() => {
      fetchCities();
      fetchZones();
      fetchReport();
    });

    return {
      today, date, loading, pdfLoading, search, report, totals, cities, zones, zoneCities, cityId, zoneId, cityLabel,
      page, perPage, pagination, pageNumbers, goTo, onFilterChange, emptyMessage, fetchReport, onCityChange, onZoneChange, openSummaryPdf, openDriverPdf, expanded, toggle, money, pct, fmt, tdlTone,
    };
  },
});
</script>

<style scoped>
/* tokens from assets/css/theme/_tokens.scss */
.dr {
  min-width: 0;
  max-width: 100%;
  padding-bottom: var(--ui-space-6);
}
.dr-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: var(--ui-space-5);
}
.dr-title {
  margin: 0;
  font-size: clamp(1.5rem, 2.6vw, 2.1rem);
  font-weight: var(--ui-fw-black);
  letter-spacing: -0.02em;
}
.dr-sub {
  margin: 6px 0 0;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
}
.dr-tools {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 10px;
}
.dr-tools .btn i {
  margin-right: 4px;
}
.dr-date {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-bold);
}
.dr-date input {
  height: 40px;
  min-width: 170px;
}
.dr-city {
  min-width: 220px;
}
.dr-city :deep(.vs__dropdown-toggle) {
  min-height: 40px;
  background: var(--ui-surface);
}
.dr-city-name {
  color: var(--ui-text-muted);
  font-weight: var(--ui-fw-bold);
}

.dr-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--ui-space-4);
  margin-bottom: var(--ui-space-5);
}
.dr-kpi {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-card);
}
.dr-kpi.is-hot {
  border-color: rgba(242, 106, 27, 0.45);
}
.dr-kpi strong {
  display: block;
  font-size: 1.5rem;
  font-weight: var(--ui-fw-black);
  line-height: 1.1;
}
.dr-kpi span {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
}
.dr-kpi-icon {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  font-size: 22px;
}
.tone-info {
  background: var(--ui-info-soft);
  color: var(--ui-info);
}
.tone-success {
  background: var(--ui-success-soft);
  color: var(--ui-success);
}
.tone-warning {
  background: var(--ui-warning-soft);
  color: var(--ui-warning-strong);
}
.tone-orange {
  background: #fde3d0;
  color: #d9560f;
}

.dr-card {
  min-width: 0;
  max-width: 100%;
  padding: var(--ui-space-5);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-card);
}
.dr-filters {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: var(--ui-space-4);
}
.dr-period {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
}
.dr-period b {
  color: var(--ui-text);
}
.dr-search {
  position: relative;
  width: 320px;
  max-width: 100%;
}
.dr-search i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--ui-text-faint);
  pointer-events: none;
}
.dr-search input {
  width: 100%;
  height: 40px;
  padding: 0 14px 0 38px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface-sunken);
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
  outline: none;
}
.dr-search input:focus {
  border-color: var(--ui-accent);
  background: var(--ui-surface);
  box-shadow: var(--ui-focus-ring);
}

/* Table: same code as the PDF (blue head, orange "À retourner") */
.dr-table-wrap {
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-md);
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
/* wide table: it scrolls inside its card instead of overflowing the page */
.dr-table {
  min-width: 1080px;
}
.dr-table th,
.dr-table td {
  padding: 10px 8px;
}
.dr-table th:first-child,
.dr-table td:first-child {
  min-width: 170px;
  padding-left: 14px;
}
.dr-total-note {
  display: block;
  font-weight: var(--ui-fw-bold);
  opacity: 0.8;
}
/* !important: the global table theme otherwise greys the head text (unreadable on the blue band) */
.dr-table thead th {
  --bs-table-bg: #0b4aa6;
  --bs-table-color: #fff;
  background: #0b4aa6 !important;
  color: #fff !important;
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-heavy);
  line-height: 1.25;
  vertical-align: middle;
  border-color: rgba(255, 255, 255, 0.18);
}
.dr-table thead th.dr-hot-head {
  background: #f26a1b !important;
}
.dr-table td {
  font-size: var(--ui-fs-sm);
  font-variant-numeric: tabular-nums;
}
.dr-table tbody tr:nth-child(even) td {
  background: var(--ui-surface-sunken);
}
.dr-table td.dr-hot {
  background: #fde3d0 !important;
  color: #1b2a4a;
}
.dr-table tfoot td {
  background: #0b4aa6;
  color: #fff;
  font-weight: var(--ui-fw-black);
  border: 0;
}
.dr-table tfoot td.dr-hot-total {
  background: #f26a1b;
}
.dr-tag {
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface-sunken);
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-bold);
}
.dr-tdl {
  display: inline-block;
  min-width: 64px;
  padding: 2px 8px;
  border-radius: var(--ui-radius-pill);
  font-weight: var(--ui-fw-bold);
}
.dr-tdl.is-good {
  background: var(--ui-success-soft);
  color: var(--ui-success);
}
.dr-tdl.is-mid {
  background: var(--ui-warning-soft);
  color: var(--ui-warning-strong);
}
.dr-tdl.is-low {
  background: var(--ui-danger-soft, #fde2e1);
  color: var(--ui-danger, #c62828);
}
.dr-received {
  display: inline-block;
  padding: 2px 10px;
  border-radius: var(--ui-radius-pill);
  background: var(--ui-warning-soft);
  color: var(--ui-warning-strong);
  font-weight: var(--ui-fw-bold);
}
.dr-received.is-done {
  background: var(--ui-success-soft);
  color: var(--ui-success);
}
.dr-act {
  white-space: nowrap;
}
.dr-link {
  padding: 0 8px;
  border: 0;
  border-bottom: 1px dashed currentColor;
  background: none;
  color: var(--ui-accent);
  font-weight: var(--ui-fw-bold);
  cursor: pointer;
}
.dr-table tr.dr-detail-row > td {
  padding: 12px 14px;
  background: var(--ui-surface-sunken) !important;
}
.dr-detail h6 {
  margin: 0 0 6px;
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-black);
}
.dr-detail + .dr-detail {
  margin-top: 12px;
}
.dr-detail .table th,
.dr-detail .table td {
  padding: 4px 8px;
  font-size: var(--ui-fs-xs);
  background: transparent !important;
  color: var(--ui-text) !important;
}
.dr-empty {
  padding: 36px 16px !important;
  color: var(--ui-text-muted);
  text-align: center;
}
.dr-empty i {
  display: block;
  margin-bottom: 6px;
  font-size: 28px;
}
.dr-legend {
  margin: var(--ui-space-4) 0 0;
  padding-left: 18px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
}
.dr-legend b {
  color: var(--ui-text);
}
.dr-legend .text-orange {
  color: #d9560f;
}

/* Pagination (server side) */
.dr-pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px 16px;
  margin-top: var(--ui-space-4);
}
.dr-pager-info {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
}
.dr-pager-size {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
}
.dr-pager-size label {
  margin: 0;
}
.dr-pager-size select {
  width: auto;
}
.dr-pager-pages {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.dr-page-btn {
  display: inline-grid;
  place-items: center;
  min-width: 36px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-md);
  background: var(--ui-surface);
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
  cursor: pointer;
}
.dr-page-btn:hover:not(:disabled):not(.is-active) {
  background: var(--ui-surface-sunken);
}
.dr-page-btn.is-active {
  border-color: #0b4aa6;
  background: #0b4aa6;
  color: #fff;
}
.dr-page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 991.98px) {
  .dr-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .dr-filters {
    align-items: stretch;
  }
}

/* Phones: each driver becomes a card (label : value) instead of a 14-column table */
@media (max-width: 767.98px) {
  .dr-card {
    padding: var(--ui-space-4);
  }
  .dr-table-wrap {
    overflow: visible;
    border: 0;
  }
  .dr-table {
    min-width: 0;
  }
  .dr-table thead {
    display: none;
  }
  .dr-table,
  .dr-table tbody,
  .dr-table tfoot,
  .dr-table tr,
  .dr-table td {
    display: block;
    width: 100%;
  }
  .dr-table tbody tr,
  .dr-table tfoot tr {
    margin-bottom: 12px;
    border: 1px solid var(--ui-border);
    border-radius: var(--ui-radius-md);
    overflow: hidden;
    background: var(--ui-surface);
  }
  .dr-table tbody tr:nth-child(even) td {
    background: transparent;
  }
  .dr-table td,
  .dr-table th:first-child,
  .dr-table td:first-child {
    min-width: 0;
    padding: 8px 14px;
  }
  .dr-table td[data-label] {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    text-align: right !important;
    border-top: 1px solid var(--ui-border);
  }
  .dr-table td[data-label]::before {
    content: attr(data-label);
    flex: 0 0 auto;
    color: var(--ui-text-muted);
    font-size: var(--ui-fs-xs);
    font-weight: var(--ui-fw-bold);
    text-align: left;
  }
  .dr-table td.dr-driver {
    border-top: 0;
    background: var(--ui-surface-sunken);
  }
  .dr-table td.dr-hot {
    background: #fde3d0 !important;
  }
  .dr-table tfoot tr {
    background: #0b4aa6;
  }
  .dr-table tfoot td {
    border-color: rgba(255, 255, 255, 0.2);
  }
  .dr-table tfoot td[data-label]::before {
    color: rgba(255, 255, 255, 0.75);
  }
  .dr-table tfoot td.dr-hot-total {
    background: #f26a1b;
  }
  .dr-table td.dr-actions:empty {
    display: none;
  }
  .dr-total-note {
    display: inline;
    margin-left: 6px;
  }
  .dr-pager {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }
  .dr-pager-size,
  .dr-pager-pages {
    justify-content: center;
  }
}
@media (max-width: 575.98px) {
  .dr-kpis {
    grid-template-columns: minmax(0, 1fr);
  }
  .dr-tools,
  .dr-date,
  .dr-city,
  .dr-date input,
  .dr-tools .btn,
  .dr-search {
    width: 100%;
  }
  .dr-period {
    width: 100%;
  }
}
</style>
